(() => {
	if (customElements.get('map-locations-static')) return;

	const DEFAULT_MARKER_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">' +
		'<path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.3 21.7 0 14 0z" fill="#8EC044"/>' +
		'<circle cx="14" cy="14" r="6" fill="#ffffff"/>' +
		'</svg>'
	)}`;

	const ACTIVE_MARKER_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">' +
		'<path d="M18 0C8.1 0 0 8.1 0 18c0 13.5 18 28 18 28s18-14.5 18-28C36 8.1 27.9 0 18 0z" fill="#1E2324"/>' +
		'<path d="M18 3C9.7 3 3 9.7 3 18c0 11.3 15 24 15 24s15-12.7 15-24C33 9.7 26.3 3 18 3z" fill="#8EC044"/>' +
		'<circle cx="18" cy="18" r="7" fill="#ffffff"/>' +
		'</svg>'
	)}`;

	customElements.define('map-locations-static', class MapLocationsStatic extends HTMLElement {
		connectedCallback() {
			this.init();
		}

		init = () => {
			this.apiKey = this.getAttribute('google-maps-api-key');
			this.zoom = parseInt(this.getAttribute('zoom'), 10) || 14;
			this.focusZoom = Math.max(this.zoom, 15);
			this.staticZoom = parseInt(this.getAttribute('static-zoom'), 10) || 6;
			this.markerImageUrl = this.getAttribute('marker-image-url') || null;
			this.defaultMarkerUrl = this.getAttribute('default-marker-url') || null;
			this.sectionId = this.getAttribute('section-id');

			this.cards = [...this.querySelectorAll('.js-mls-card')];
			this.locations = this.cards.map(card => ({
				lat: parseFloat(card.dataset.lat),
				lng: parseFloat(card.dataset.lng),
				title: card.dataset.title,
				el: card,
			})).filter(loc => !isNaN(loc.lat) && !isNaN(loc.lng));

			this.staticOverlay = this.querySelector('.js-mls-static-overlay');
			this.staticImg = this.querySelector('.js-mls-static-img');
			this.resetButton = this.querySelector('.js-mls-reset');
			this.searchForm = this.querySelector('.js-mls-search-form');
			this.searchInput = this.querySelector('.js-mls-search-input');
			this.searchSubmit = this.querySelector('.js-mls-search-submit');
			this.locateButton = this.querySelector('.js-mls-locate-button');
			this.searchStatus = this.querySelector('.js-mls-search-status');
			this.mapContainer = this.querySelector(`#Map-${this.sectionId}`);

			this.renderStaticMap();

			this.staticOverlay?.addEventListener('click', () => this.loadInteractiveMap());

			this.cards.forEach(card => {
				card.addEventListener('click', (event) => {
					// Let links inside the card (View Shop Page / Get Directions) behave normally.
					if (event.target.closest('a')) return;
					this.handleCardClick(card);
				});
				card.addEventListener('keydown', (event) => {
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						this.handleCardClick(card);
					}
				});
			});

			this.resetButton?.addEventListener('click', () => this.handleReset());
			this.locateButton?.addEventListener('click', () => this.handleFindNearMe());
			this.searchForm?.addEventListener('submit', (event) => {
				event.preventDefault();
				this.handlePostcodeSearch();
			});
		};

		// --- Static map (default state, no JS API load, no geocoding) ---
		renderStaticMap = () => {
			if (!this.staticImg || !this.locations.length) return;

			if (!this.apiKey) {
				this.staticImg.remove();
				return;
			}

			// Use the same pin shape as the interactive map (a hosted SVG asset, since
			// Static Maps needs a public URL and can't fetch a data: URI) so markers
			// look identical in both states, instead of Google's built-in size:small pin.
			// scale:0.5 offsets the global scale=2 (retina) param below, which would
			// otherwise double the icon's native pixel size on the rendered image.
			const iconUrl = this.markerImageUrl || this.defaultMarkerUrl;
			const markers = iconUrl
				? this.locations.map(loc => `markers=icon:${encodeURIComponent(iconUrl)}%7Cscale:0.5%7C${loc.lat},${loc.lng}`).join('&')
				: this.locations.map(loc => `markers=size:small%7Ccolor:0x8EC044%7C${loc.lat},${loc.lng}`).join('&');

			const params = new URLSearchParams({
				size: '640x400',
				scale: '2',
				key: this.apiKey,
			});

			let src = `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}&${markers}`;
			if (this.locations.length === 1) {
				src += `&center=${this.locations[0].lat},${this.locations[0].lng}&zoom=${this.staticZoom}`;
			}

			this.staticImg.src = src;
		};

		// --- Interactive map (loaded only on click / search / locate) ---
		loadInteractiveMap = async (focusLocation) => {
			if (this.interactiveLoaded) {
				if (focusLocation) this.selectLocation(focusLocation);
				return;
			}
			this.interactiveLoaded = true;
			this.staticOverlay?.remove();

			await this.ensureGoogleMapsLoaded();

			this.map = new google.maps.Map(this.mapContainer, {
				zoom: this.zoom,
			});

			this.markers = this.locations.map(loc => {
				const marker = new google.maps.Marker({
					map: this.map,
					position: { lat: loc.lat, lng: loc.lng },
					title: loc.title,
					icon: this.iconFor(loc, false),
				});

				const infoWindow = new google.maps.InfoWindow({
					content: this.infoWindowContent(loc),
					headerDisabled: true,
					ariaLabel: loc.title,
				});

				marker.addListener('click', () => this.selectLocation(loc, infoWindow));

				loc.marker = marker;
				loc.infoWindow = infoWindow;
				return marker;
			});

			if (this.markers.length > 1) {
				this.bounds = new google.maps.LatLngBounds();
				this.markers.forEach(marker => this.bounds.extend(marker.getPosition()));
				this.map.fitBounds(this.bounds);
			} else if (this.markers.length === 1) {
				this.map.setCenter(this.markers[0].getPosition());
				this.map.setZoom(this.zoom);
			}

			if (focusLocation) this.selectLocation(focusLocation);
		};

		infoWindowContent = (loc) => {
			const addressEl = loc.el?.querySelector('.mls-card-address');
			const addressHtml = addressEl ? addressEl.innerHTML : '';
			const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
			const shopLink = loc.el?.querySelector('.mls-card-link:not([target])');
			const shopUrl = shopLink ? shopLink.getAttribute('href') : null;

			return `
				<div class="mls-infowindow">
					<h4 class="mls-infowindow-title">${loc.title}</h4>
					${addressHtml ? `<div class="mls-infowindow-address">${addressHtml}</div>` : ''}
					<div class="mls-infowindow-actions">
						${shopUrl ? `<a href="${shopUrl}" class="mls-infowindow-link">View Shop Page</a>` : ''}
						<a href="${directionsUrl}" target="_blank" rel="noopener" class="mls-infowindow-link mls-infowindow-link--primary">Get Directions</a>
					</div>
				</div>
			`;
		};

		iconFor = (loc, isActive) => {
			if (this.markerImageUrl) {
				return {
					url: this.markerImageUrl,
					scaledSize: isActive ? new google.maps.Size(36, 46) : new google.maps.Size(28, 36),
					anchor: isActive ? new google.maps.Point(18, 46) : new google.maps.Point(14, 36),
				};
			}
			return isActive
				? { url: ACTIVE_MARKER_SVG, scaledSize: new google.maps.Size(36, 46), anchor: new google.maps.Point(18, 46) }
				: { url: DEFAULT_MARKER_SVG, scaledSize: new google.maps.Size(28, 36), anchor: new google.maps.Point(14, 36) };
		};

		ensureGoogleMapsLoaded = () => {
			if (window.google?.maps) return Promise.resolve();
			if (window._mlsGoogleMapsPromise) return window._mlsGoogleMapsPromise;

			window._mlsGoogleMapsPromise = new Promise((resolve, reject) => {
				const script = document.createElement('script');
				// Loading Geocoder via the JS API (not the raw REST endpoint) is required
				// for compatibility with HTTP-referrer-restricted API keys.
				script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=geometry`;
				script.async = true;
				script.onload = resolve;
				script.onerror = reject;
				document.head.appendChild(script);
			});

			return window._mlsGoogleMapsPromise;
		};

		// Highlights the pin (bigger icon, opens info window), zooms in on it,
		// and marks the matching card as active.
		selectLocation = (loc, infoWindowOverride) => {
			if (!loc.marker || !this.map) return;

			if (this.activeLocation && this.activeLocation !== loc) {
				this.activeLocation.marker.setIcon(this.iconFor(this.activeLocation, false));
				this.activeLocation.el?.classList.remove('is-active');
			}

			loc.marker.setIcon(this.iconFor(loc, true));
			loc.el?.classList.add('is-active');
			this.activeLocation = loc;

			this.map.panTo(loc.marker.getPosition());
			this.map.setZoom(this.focusZoom);

			this.activeInfoWindow?.close();
			const infoWindow = infoWindowOverride || loc.infoWindow;
			infoWindow?.open({ anchor: loc.marker, map: this.map, shouldFocus: false });
			this.activeInfoWindow = infoWindow;

			this.resetButton?.classList.remove('visually-hidden');
		};

		handleCardClick = async (card) => {
			const loc = this.locations.find(l => l.el === card);
			if (!loc) return;
			await this.loadInteractiveMap(loc);
		};

		handleReset = () => {
			if (this.activeLocation) {
				this.activeLocation.marker.setIcon(this.iconFor(this.activeLocation, false));
				this.activeLocation.el?.classList.remove('is-active');
				this.activeLocation = null;
			}
			this.activeInfoWindow?.close();
			if (this.bounds && this.map) {
				this.map.fitBounds(this.bounds);
				this.resetButton?.classList.add('visually-hidden');
			}
		};

		// --- Sorting / status helpers shared by postcode search + geolocation ---
		setStatus = (message, isError) => {
			if (!this.searchStatus) return;
			this.searchStatus.textContent = message || '';
			this.searchStatus.classList.toggle('is-error', !!isError);
		};

		sortListByOrigin = (originLat, originLng) => {
			// Mutate distance directly onto the original location objects (rather than
			// spreading into copies) so any live `marker` / `infoWindow` references
			// attached later by loadInteractiveMap stay intact on the returned nearest match.
			this.locations.forEach(loc => {
				loc.distance = this.haversineDistance(originLat, originLng, loc.lat, loc.lng);
			});
			const sorted = [...this.locations].sort((a, b) => a.distance - b.distance);

			const list = this.querySelector('.mls-list');
			sorted.forEach(loc => {
				list.appendChild(loc.el);
				const existingBadge = loc.el.querySelector('.mls-distance-badge');
				if (existingBadge) existingBadge.remove();
				const badge = document.createElement('span');
				badge.className = 'mls-distance-badge';
				badge.textContent = `${loc.distance.toFixed(1)} mi`;
				loc.el.querySelector('.mls-card-title')?.appendChild(badge);
			});

			return sorted[0];
		};

		// --- Postcode / town search via google.maps.Geocoder (JS API — works with
		// HTTP-referrer-restricted keys, unlike the raw REST /geocode/json endpoint) ---
		handlePostcodeSearch = async () => {
			const query = this.searchInput?.value?.trim();
			if (!query) return;
			if (!this.apiKey) {
				this.setStatus('Search is unavailable right now.', true);
				return;
			}

			this.setStatus('Searching…');
			this.searchSubmit.disabled = true;

			try {
				await this.ensureGoogleMapsLoaded();

				if (!this.geocoder) {
					this.geocoder = new google.maps.Geocoder();
				}

				const result = await this.geocoder.geocode({
					address: query,
					region: 'gb',
					componentRestrictions: { country: 'GB' },
				});

				if (!result?.results?.length) {
					this.setStatus("Couldn't find that location. Try a different postcode or town.", true);
					this.searchSubmit.disabled = false;
					return;
				}

				const location = result.results[0].geometry.location;
				const lat = location.lat();
				const lng = location.lng();

				const nearest = this.sortListByOrigin(lat, lng);
				this.setStatus(nearest ? `Nearest: ${nearest.title} (${nearest.distance.toFixed(1)} mi)` : '');

				if (nearest) {
					await this.loadInteractiveMap(nearest);
				}
			} catch (error) {
				this.setStatus("Couldn't find that location. Try a different postcode or town.", true);
			} finally {
				this.searchSubmit.disabled = false;
			}
		};

		// --- "Use my current location" via browser geolocation ---
		handleFindNearMe = () => {
			if (!navigator.geolocation || !this.locations.length) return;

			this.locateButton.disabled = true;
			this.setStatus('Locating…');

			navigator.geolocation.getCurrentPosition(
				async (position) => {
					const { latitude, longitude } = position.coords;
					const nearest = this.sortListByOrigin(latitude, longitude);
					this.setStatus(nearest ? `Nearest: ${nearest.title} (${nearest.distance.toFixed(1)} mi)` : '');
					this.locateButton.disabled = false;

					if (nearest) {
						await this.loadInteractiveMap(nearest);
					}
				},
				() => {
					this.setStatus("Couldn't get your location. Check your browser's location permission.", true);
					this.locateButton.disabled = false;
				}
			);
		};

		haversineDistance = (lat1, lon1, lat2, lon2) => {
			const R = 3958.8; // miles
			const dLat = (lat2 - lat1) * Math.PI / 180;
			const dLon = (lon2 - lon1) * Math.PI / 180;
			const a = Math.sin(dLat / 2) ** 2 +
				Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
				Math.sin(dLon / 2) ** 2;
			return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		};
	});
})();
